import { useEffect, useRef, useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import { DropdownPanel, IconButton, Icons, cn } from '@workflow/ui'
import { navigate } from '@/app/router'
import { useMarkAllNotificationsRead, useMarkNotificationRead, useMembers, useNotifications, useSession } from '@/lib/api/hooks'
import { formatTimestamp } from '@/lib/format'
import { personName } from '@/lib/people'

/**
 * The bell: the words that traveled to this account, gathered where the
 * hand already looks. The feed is the notification table read backwards —
 * newest first, unheard counted on the bell itself, a click marking heard
 * and following the word's destination. Above it, for administrators only,
 * the one derived truth that predates the table: accounts waiting for
 * review, which are decisions rather than news.
 */
export function NotificationsMenu() {
  const session = useSession()
  const isAdmin = session.data?.account?.role === 'admin'
  const isPlayer = session.data?.account?.role === 'player'
  // The waiting room is the administrators' business; nobody else asks for it.
  const members = useMembers(isAdmin)
  const notifications = useNotifications()
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const waiting = (members.data ?? []).filter((member) => member.status === 'pending')
  const feed = notifications.data?.notifications ?? []
  const unread = (notifications.data?.unread ?? 0) + waiting.length

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!session.data?.account) return null

  const follow = (target: string | null, close: () => void) => {
    if (target === null) return
    if (target.startsWith('/announcements/')) {
      close()
      navigate({ name: 'announcements', id: target.slice('/announcements/'.length) })
      return
    }
    // A player's words point at their portal; the work is where they land.
    if (isPlayer) {
      close()
      navigate({ name: 'work' })
      return
    }
    if (target.startsWith('/jobs/')) {
      const id = target.slice('/jobs/'.length)
      if (id) {
        close()
        navigate({ name: 'job', id })
      }
    }
  }

  return (
    <div ref={rootRef} className="pointer-events-auto relative">
      <AnimatePresence>
        {open ? (
          <DropdownPanel
            style={{ transformOrigin: 'top right' }}
            role="menu"
            aria-label="Notifications"
            className="absolute right-0 top-[calc(100%+8px)] z-40 max-h-[min(70vh,32rem)] w-80 overflow-y-auto rounded-xl border bg-surface p-1 shadow-chip"
          >
            <div className="flex items-center justify-between px-2.5 pt-2 pb-1">
              <p className="text-micro text-muted-foreground uppercase">Notifications</p>
              {unread > 0 ? (
                <button
                  type="button"
                  className="text-caption text-muted-foreground hover:text-foreground"
                  onClick={() => markAll.mutate(undefined)}
                >
                  Mark all read
                </button>
              ) : null}
            </div>

            {isAdmin ? (
              <>
                {members.isLoading ? null : waiting.length > 0 ? (
                  <p className="px-2.5 pt-1 pb-1 text-micro text-muted-foreground uppercase">Waiting for a decision</p>
                ) : null}
                {waiting.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpen(false)
                      navigate({ name: 'people' })
                    }}
                    className={cn(
                      'flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors duration-fast hover:bg-tint',
                    )}
                  >
                    <span className="mt-0.5 flex shrink-0 items-center text-muted-foreground">
                      <Icons.review />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-ui text-foreground">
                        {personName(member)} is waiting for a decision
                      </span>
                      <span className="block text-caption text-muted-foreground">
                        Review their profile · {formatTimestamp(member.createdAt)}
                      </span>
                    </span>
                  </button>
                ))}
              </>
            ) : null}

            {notifications.isLoading ? (
              <p className="px-2.5 py-3 text-caption text-muted-foreground">Listening…</p>
            ) : feed.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-7 text-center">
                <p className="font-display text-lg text-foreground">All quiet.</p>
                <p className="mt-1 text-caption text-muted-foreground">Nothing has traveled to you yet.</p>
              </div>
            ) : (
              feed.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    markRead.mutate({ id: notification.id })
                    follow(notification.target, () => setOpen(false))
                  }}
                  className={cn(
                    'flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors duration-fast hover:bg-tint',
                  )}
                >
                  <span className="mt-1.5 shrink-0">
                    <span
                      className={cn('block size-2 rounded-full', notification.readAt === null ? 'bg-foreground' : 'bg-foreground/15')}
                      aria-hidden
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-ui', notification.readAt === null ? 'text-foreground' : 'text-muted-foreground')}>
                      {notification.sentence}
                    </span>
                    <span className="block text-caption text-muted-foreground">{formatTimestamp(notification.createdAt)}</span>
                  </span>
                </button>
              ))
            )}
          </DropdownPanel>
        ) : null}
      </AnimatePresence>

      <IconButton
        label="Notifications"
        tooltipSide="bottom"
        active={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="relative">
          <Icons.bell size="md" />
          {unread > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-foreground" aria-hidden />
          ) : null}
        </span>
      </IconButton>
    </div>
  )
}
